import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { CompanyDocument, downloadFile } from "winslow-core";
import { Button } from "../../../ds2/components";
import {
  apiGetCompanyDocumentsForWorkspace,
  apiUploadCompanyDocumentsForWorkspace,
  apiUpdateCompanyDocument,
  apiDeleteCompanyDocument,
} from "../../../services/CompanyDocumentsService";
import { selectUser } from "../../../redux/features/appData/appDataSlice";
import { WazToolbar } from "../toolbar/WazToolbar";
import { AppDocumentsUploadButton } from "../../app/app-documents/AppDocumentsUploadButton";
import { AppChanges } from "../../app/app-changes/AppChanges";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import "./WazDocuments.css";
import Modal from "../../shared/Modal";
import { DownloadIcon, EditIcon, EyeIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Icon } from "../../../ds2/components/Icon";
import { DocumentDrawer } from "./DocumentDrawer";
import DeleteDocumentConfirmModal from "../../app/app-documents/DeleteDocumentConfirmModal";
import { DocumentPreviewModal } from "./DocumentPreviewModal";

type WazDocumentsProps = {};

type UploadInfo = {
  name: string;
  description: string;
  requiresSignatures: boolean;
};

export const WazDocuments = ({}: WazDocumentsProps) => {
  const storeUser = useSelector(selectUser);
  const [documents, setDocuments] = useState<CompanyDocument[] | undefined>(
    undefined
  );
  const [filter, setFilter] = useState("");
  const [addDocument, setAddDocument] = useState(false);
  const [info, setInfo] = useState<UploadInfo>({
    name: "",
    description: "",
    requiresSignatures: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editDocumentId, setEditDocumentId] = useState<string | null>(null);
  const [editInfo, setEditInfo] = useState<UploadInfo>({
    name: "",
    description: "",
    requiresSignatures: false,
  });
  const [deletingDocument, setDeletingDocument] =
    useState<CompanyDocument | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewingDocument, setPreviewingDocument] = useState<CompanyDocument | null>(null);

  useEffect(() => {
    if (!storeUser?.currentWorkspaceId) {
      setDocuments(undefined);
      return;
    }
    (async () => {
      const docs = await apiGetCompanyDocumentsForWorkspace(
        storeUser.currentWorkspaceId || ""
      );
      if (docs) {
        docs.sort((a, b) => a.name.localeCompare(b.name));
        setDocuments(docs);
      }
    })();
  }, [storeUser]);

  const filteredDocuments = documents?.filter((doc) => {
    const search = filter.toLowerCase();
    return (
      doc.name.toLowerCase().includes(search) ||
      (doc.fileName && doc.fileName.toLowerCase().includes(search)) ||
      (doc.description && doc.description.toLowerCase().includes(search))
    );
  });

  const handleSelectedFile = (file: File) => {
    setSelectedFile(file);

    if (!info.name) {
      // Get filename without extension
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setInfo({ ...info, name: fileNameWithoutExtension });
    }
  };

  const handleUploadSubmit = async () => {
    if (!storeUser?.currentWorkspaceId) {
      toast("No workspace selected", toastFail);
      return;
    }
    if (!selectedFile) {
      toast("No file selected", toastFail);
      return;
    }
    if (!info.name) {
      toast("Please provide a document name", toastFail);
      return;
    }
    
    // Check file size (100MB limit)
    const maxFileSize = 100 * 1024 * 1024; // 100MB
    console.log("File size check:", {
      fileSize: selectedFile.size,
      fileSizeMB: (selectedFile.size / (1024 * 1024)).toFixed(2),
      maxFileSize,
      maxFileSizeMB: (maxFileSize / (1024 * 1024)).toFixed(2),
      isTooLarge: selectedFile.size > maxFileSize
    });
    
    if (selectedFile.size > maxFileSize) {
      toast("File size too large. Maximum size is 100MB", toastFail);
      return;
    }
    
    setUploading(true);
    const payload: Partial<CompanyDocument> = {
      workspaceId: storeUser.currentWorkspaceId,
      name: info.name,
      description: info.description,
      requiresSignatures: info.requiresSignatures,
    };
    
    try {
      const response = await apiUploadCompanyDocumentsForWorkspace(
        storeUser.currentWorkspaceId,
        payload,
        selectedFile
      );
      setUploading(false);
      if (!response) {
        toast("Error creating company document", toastFail);
        return;
      }
      const companyDocuments = response.data.data as CompanyDocument[];
      setAddDocument(false);
      setInfo({ name: "", description: "", requiresSignatures: false });
      setSelectedFile(null);
      setDocuments((prev) => {
        const updated = prev ? [...prev, ...companyDocuments] : companyDocuments;
        updated.sort((a, b) => a.name.localeCompare(b.name));
        return updated;
      });
    } catch (error: any) {
      setUploading(false);
      console.error("Upload error:", error);
      
      if (error.response?.status === 413) {
        toast("File too large. Please choose a smaller file (max 100MB)", toastFail);
      } else if (error.response?.status === 400) {
        toast("Invalid file type. Please upload documents (PDF, DOC, DOCX), images (JPG, PNG, GIF, etc.), or videos (MP4, MOV, AVI, etc.)", toastFail);
      } else {
        toast("Error uploading document. Please try again.", toastFail);
      }
    }
  };

  const handlePreviewClick = (doc: CompanyDocument) => {
    setPreviewingDocument(doc);
  };

  const handleClosePreview = () => {
    setPreviewingDocument(null);
  };

  const handleEditClick = (doc: CompanyDocument) => {
    setEditDocumentId(doc.id);
    setEditInfo({
      name: doc.name,
      description: doc.description || "",
      requiresSignatures: doc.requiresSignatures || false,
    });
  };

  const handleEditSubmit = async () => {
    if (!editDocumentId) return;
    setUploading(true);
    const payload: Partial<CompanyDocument> = {
      id: editDocumentId,
      name: editInfo.name,
      description: editInfo.description,
      requiresSignatures: editInfo.requiresSignatures,
    };
    const updatedDocument = await apiUpdateCompanyDocument(payload);
    setUploading(false);
    if (!updatedDocument) {
      toast("Error updating company document", toastFail);
      return;
    }
    setEditDocumentId(null);
    setEditInfo({ name: "", description: "", requiresSignatures: false });
    setDocuments((prev) => {
      if (!prev) return prev;
      const updated = prev.map((doc) =>
        doc.id === updatedDocument.id ? { ...doc, ...updatedDocument } : doc
      );
      updated.sort((a, b) => a.name.localeCompare(b.name));
      return updated;
    });
  };

  const handleDeleteClick = (doc: CompanyDocument) => {
    setDeletingDocument(doc);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDocument) return;
    setUploading(true);
    const response = await apiDeleteCompanyDocument(deletingDocument.id);
    setUploading(false);
    setShowDeleteModal(false);
    setDeletingDocument(null);
    if (!response) {
      toast("Error deleting company document", toastFail);
      return;
    }
    setDocuments((prev) => prev?.filter((d) => d.id !== response.id));
  };

  const allowSave = !uploading && info?.name && selectedFile;
  const allowEditSave = !uploading && editInfo?.name;

  return (
    <div className="wazawan-documents">
      <WazToolbar />
      <div className="waz-documents-content">
        <h1 className="waz-documents-title">Company Documents</h1>
        <div className="waz-documents-toolbar-row">
          <input
            type="text"
            className="waz-documents-filter-input"
            placeholder="Filter by name, filename, or description..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button
            variant="primary"
            onClick={() => setAddDocument((v) => !v)}
            disabled={addDocument || !!editDocumentId}
            leftIcon={<PlusIcon />}
          >
            Document
          </Button>
          
          {/* <button
            className="button-save waz-documents-add-btn"
            onClick={() => setAddDocument((v) => !v)}
            disabled={addDocument || !!editDocumentId}>
            + Add Document
          </button> */}
        </div>
        {/* Document Drawer */}
        <DocumentDrawer
          isOpen={addDocument || !!editDocumentId}
          onClose={() => {
            if (addDocument) {
              setAddDocument(false);
              setInfo({
                name: "",
                description: "",
                requiresSignatures: false,
              });
              setSelectedFile(null);
            } else if (editDocumentId) {
              setEditDocumentId(null);
              setEditInfo({
                name: "",
                description: "",
                requiresSignatures: false,
              });
            }
          }}
          mode={addDocument ? "add" : "edit"}
          info={addDocument ? info : editInfo}
          setInfo={addDocument ? setInfo : setEditInfo}
          selectedFile={selectedFile}
          onFileSelect={handleSelectedFile}
          onSubmit={addDocument ? handleUploadSubmit : handleEditSubmit}
          onCancel={() => {
            if (addDocument) {
              setAddDocument(false);
              setInfo({
                name: "",
                description: "",
                requiresSignatures: false,
              });
              setSelectedFile(null);
            } else if (editDocumentId) {
              setEditDocumentId(null);
              setEditInfo({
                name: "",
                description: "",
                requiresSignatures: false,
              });
            }
          }}
          uploading={uploading}
          allowSave={addDocument ? !!allowSave : !!allowEditSave}
          editDocumentId={editDocumentId}
        />
        <div className="waz-documents-table-container">
          <table className="waz-documents-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Filename</th>
                <th>Description</th>
                {/* <th>Public</th> */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments && filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="waz-documents-table-row">
                    <td>{doc.name}</td>
                    <td>{doc.fileName}</td>
                    <td>{doc.description?.substring(0, 500) || ""}</td>
                    {/* <td>{doc.isPublic ? "Yes" : "No"}</td> */}
                    <td className="waz-documents-table-actions">
                      {doc.cloudURL && (
                        <> 
                          <Icon 
                            icon={<DownloadIcon />} 
                            border={false}
                            onClick={() =>
                              downloadFile(
                                doc.cloudURL,
                                doc.fileName || "document"
                              )
                            }
                          />
                          {/* <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              downloadFile(
                                doc.cloudURL,
                                doc.fileName || "document"
                              )
                            }
                          >
                            Download
                          </Button> */}
                          <Icon 
                            icon={<EyeIcon />} 
                            border={false}
                            onClick={() =>handlePreviewClick(doc)}
                          />

                          {/* <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>handlePreviewClick(doc)}>
                            Preview
                          </Button> */}
                          
                          {/* <button
                            className="button-save"
                            onClick={() =>
                              downloadFile(
                                doc.cloudURL,
                                doc.fileName || "document"
                              )
                            }
                          >
                            Download
                          </button>
                          <button
                            className="button-save"
                            onClick={() =>handlePreviewClick(doc)}
                          >
                            Preview
                          </button> */}
                        </>
                      )}

                      <Icon 
                        icon={<EditIcon />} 
                        border={false}
                        onClick={() => handleEditClick(doc)}
                        disabled={!!editDocumentId || addDocument}
                      />
                      
                      {/* <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditClick(doc)}
                        disabled={!!editDocumentId || addDocument}>
                        Edit
                      </Button> */}
                      
                      <Icon 
                        icon={<TrashIcon />} 
                        border={false}
                        onClick={() => handleDeleteClick(doc)}
                        disabled={!!editDocumentId || addDocument}
                      />

                      {/* <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => handleDeleteClick(doc)}
                        disabled={!!editDocumentId || addDocument}>
                        Delete
                      </Button> */}
                      
                      {/* <button
                        className="button-cancel"
                        onClick={() => handleEditClick(doc)}
                        disabled={!!editDocumentId || addDocument}>
                        Edit
                      </button>
                      <button
                        className="button-cancel"
                        onClick={() => handleDeleteClick(doc)}
                        disabled={!!editDocumentId || addDocument}>
                        Delete
                      </button> */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="waz-documents-table-empty">
                    No documents found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteDocumentConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onContinue={handleDeleteConfirm}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewingDocument}
        onClose={handleClosePreview}
        document={previewingDocument}
      />
      
                
    </div>
  );
};
